import React, { useContext } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { AuthContext } from "../contexts/AuthContext";
import LoadingLoader from "../components/UI/LoadingLoader";
import { getRolePrefix } from "../utils/routeUtils";

export const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, roles, loading } = useContext(AuthContext);
  const location = useLocation();

  if (loading) {
    // console.log("ProtectedRoute - Waiting for auth state to load...");
    return <LoadingLoader />;
  }
  
  // Check if user is authenticated
  if (!user) {
    return <Navigate to="/" />;
  }

  // Check if user has access to the current route based on their role
  const currentPath = location.pathname;
  const userRolePrefix = getRolePrefix(roles);
  
  // Normalize paths for comparison (remove trailing slashes)
  const normalizedPath = currentPath.replace(/\/$/, '') || '/';
  const normalizedPrefix = userRolePrefix.replace(/\/$/, '') || '/';
  
  // Allow certain paths that don't require role prefixes (like /payroll/)
  const allowedPathsWithoutPrefix = ['/payroll', '/master', '/campaigns', '/attendance_management'];
  
  // Check if the path is in the allowed list (starts with allowed path)
  const isAllowedPath = allowedPathsWithoutPrefix.some(path => {
    return normalizedPath === path || normalizedPath.startsWith(path + '/');
  });
  
  // If the current path doesn't match the user's role prefix, redirect them
  // Exception: allow paths that start with allowed paths without prefix
  if (!isAllowedPath) {
    if (normalizedPath !== normalizedPrefix && !normalizedPath.startsWith(normalizedPrefix + '/')) {
      return <Navigate to={userRolePrefix} replace />;
    }
  }

  // Check if user has the required roles for specific functionality
  const hasAccess =
    !allowedRoles || allowedRoles.some((role) => roles.includes(role));

  // console.log("ProtectedRoute - Auth:", !!user, "Access:", hasAccess);

  return hasAccess ? children : <Navigate to={userRolePrefix} />;
};

export const PublicRoute = ({ children }) => {
  const { user, roles } = useContext(AuthContext);

  if (user && roles.length > 0) {
    const rolePrefix = getRolePrefix(roles);
    return <Navigate to={rolePrefix} />;
  }

  return children;
};
